// Dashboard tiles: how a tile's query is run (with the dashboard's period, comparison and slicers) and how each
// kind of tile draws its result. Every number is computed in Postgres by the same engine as workbooks.
import { Link } from "react-router-dom";
import type { DSlicer, DSpec, DTile, XFilter, XGroupedResult, XOutColumn, XPeriod, XQuery, XResult } from "../api";
import { Chart, PALETTE, Spark, type Series } from "./Chart";
import { pickFromChart, pickOf, type Pick } from "./inspect";
import { fmt, fmtCompact, fmtKey, fmtRange, fmtSigned } from "./format";

export type TileRun = { main?: XResult; compare?: XResult | null; spark?: XResult | null; error?: string };
export type Plan = { tile: DTile; main: XQuery; compare?: XQuery; spark?: XQuery };

const COMPARE_LABEL = { py: "vs prior year", pp: "vs prior period", none: "" } as const;
export const PERIODS: { id: string; label: string; period: XPeriod | null }[] = [
  { id: "m1", label: "Last month", period: { unit: "month", n: 1 } },
  { id: "m3", label: "Last 3 months", period: { unit: "month", n: 3 } },
  { id: "m6", label: "Last 6 months", period: { unit: "month", n: 6 } },
  { id: "m12", label: "Last 12 months", period: { unit: "month", n: 12 } },
  { id: "q1", label: "Last quarter", period: { unit: "quarter", n: 1 } },
  { id: "ytd", label: "Year to date", period: { unit: "ytd" } },
  { id: "y1", label: "Last full year", period: { unit: "year", n: 1 } },
  { id: "m24", label: "Last 24 months", period: { unit: "month", n: 24 } },
  { id: "all", label: "All time", period: null },
];
export const periodId = (p: XPeriod | null) => (!p ? "all" : PERIODS.find((x) => x.period && x.period.unit === p.unit && (x.period.n ?? null) === (p.unit === "ytd" ? null : p.n ?? null))?.id ?? "m12");

/** The queries one tile needs: its own, the same over the comparison period (KPIs), and a trend (KPI sparklines). */
export function planTile(tile: DTile, spec: DSpec, slicers: Record<string, unknown[]>, datasets?: { id: string; columns: { id: string }[] }[]): Plan | null {
  if (!tile.query || tile.kind === "text") return null;
  const q = tile.query;
  const sliced: XFilter[] = [];
  for (const s of spec.slicers ?? []) {
    const col = s.columns[q.dataset];
    const vals = slicers[s.id];
    if (col && vals?.length && (!datasets || datasets.find((d) => d.id === q.dataset)?.columns.some((c) => c.id === col))) sliced.push({ column: col, op: "in", value: vals });
  }
  const period = (shift?: "pp" | "py"): XFilter[] => (tile.date && spec.period ? [{ column: tile.date, op: "period", value: { anchor: "data", ...spec.period, shift: shift ?? null } }] : []);
  const base = (shift?: "pp" | "py"): XQuery => ({ ...q, filters: [...(q.filters ?? []), ...sliced, ...period(shift)] });
  const kind = tile.kind === "heatmap" ? "pivot" : tile.kind === "kpi" ? "grouped" : q.kind;
  const main: XQuery = { ...base(), kind, ...(tile.kind === "kpi" ? { groups: [], subtotals: false } : {}), ...(tile.kind === "heatmap" ? { subtotals: false } : {}), ...(kind === "table" ? { limit: q.limit ?? 200 } : {}) };
  if (tile.kind !== "kpi") return { tile, main };
  const cmp = spec.compare !== "none" && tile.date && spec.period ? { ...base(spec.compare), kind: "grouped" as const, groups: [], subtotals: false } : undefined;
  const p = spec.period;
  const months = !p ? 24 : p.unit === "day" ? (p.n ?? 30) / 30 : p.unit === "month" ? p.n ?? 12 : p.unit === "quarter" ? (p.n ?? 1) * 3 : p.unit === "year" ? (p.n ?? 1) * 12 : 12;
  const spark = tile.view?.spark !== false && tile.date ? { ...base(), kind: "grouped" as const, groups: [{ column: tile.date, bucket: months <= 3 ? "week" as const : "month" as const }], subtotals: false, sort: [] } : undefined;
  return { tile, main, compare: cmp, spark };
}

/** The dates the dashboard's period resolved to, from the first tile that reports them. */
export function periodText(runs: Record<string, TileRun>) {
  for (const r of Object.values(runs)) { const p = r.main && "periods" in r.main ? r.main.periods?.[0] : undefined; if (p) return fmtRange(p.from, p.to); }
  return null;
}

// ---------------- KPI ----------------

/** Values that add up (sums and counts): an empty group is 0, a missing month is 0, and a long tail can fold into Other. */
export const additive = (c?: XOutColumn) => !!c && (c.agg === "sum" || c.agg === "count" || c.agg === "countdistinct"
  || (!!c.measure && !c.agg && c.format !== "pct" && !/avg|average|price|rate|%|days/i.test(c.name)));
const single = (r?: XResult | null) => (r && r.kind === "grouped" ? r.rows.find((x) => x.level === 0 || x.level === r.groups.length)?.values[0] ?? null : null);

export function KpiTile({ tile, run, spec }: { tile: DTile; run: TileRun; spec: DSpec }) {
  const r = run.main as XGroupedResult | undefined;
  const col = r?.values[0];
  const format = tile.view?.format ?? col?.format;
  const z = (x: number | null) => (x === null && additive(col) ? 0 : x);
  const v = r ? z(single(r)) : null, c = run.compare === undefined ? undefined : run.compare ? z(single(run.compare)) : null;
  const goodUp = tile.view?.good !== "down";
  let delta: { text: string; good: boolean | null; dir: "up" | "down" | "flat" } | null = null;
  if (c !== undefined && v !== null) {
    if (c === null || (c === 0 && format !== "pct")) delta = { text: `No data ${COMPARE_LABEL[spec.compare]}`, good: null, dir: "flat" };
    else {
      // On a base near zero (a variance that was nothing last year) a percentage is noise: show the change itself.
      const tiny = format !== "pct" && (c * v < 0 || Math.abs(c) < Math.abs(v) * 0.1);
      const d = format === "pct" ? (v - c) * 100 : (v - c) / Math.abs(c) * 100;
      const dir = Math.abs(d) < 0.05 ? "flat" : d > 0 ? "up" : "down";
      const text = tiny ? fmtSigned(v - c, format) : `${Math.abs(d).toFixed(1)}${format === "pct" ? " pts" : "%"}`;
      delta = { text: `${text} ${COMPARE_LABEL[spec.compare]}`, good: dir === "flat" ? null : (dir === "up") === goodUp, dir };
    }
  }
  const spark = run.spark && run.spark.kind === "grouped" ? run.spark.rows.filter((x) => x.level === 1).map((x) => x.values[0]) : null;
  return (
    <div className="kpi">
      <div className="kpi-top">
        <div className="kpi-v" title={v === null ? "" : fmt(v, col, format)}>{r ? <KpiNumber v={v} format={format} /> : <span className="sk sk-v" />}</div>
        {spark && <Spark values={spark} good={delta?.good ?? null} width={96} height={30} />}
      </div>
      {delta ? <span className={`kpi-d ${delta.good === null ? "" : delta.good ? "good" : "bad"}`}>{delta.dir !== "flat" && <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d={delta.dir === "up" ? "M5 1l4 6H1z" : "M5 9l4-6H1z"} fill="currentColor" /></svg>}{delta.text}</span> : <span className="kpi-d">{tile.note ?? ""}</span>}
    </div>
  );
}

/** A headline number; money carries its currency sign, set smaller so the figure reads first. */
export function KpiNumber({ v, format }: { v: number | null; format?: string }) {
  if (v === null || format !== "money") return <>{fmtCompact(v, format)}</>;
  return <>{v < 0 ? "−" : ""}<span className="kpi-cur">$</span>{fmtCompact(Math.abs(v), format)}</>;
}

// ---------------- Chart ----------------

const BUCKET_MONTHS: Record<string, number> = { month: 1, quarter: 3, year: 12 };
/** Every bucket from the first key to the last, so a month with no rows is a gap on the axis, not a missing month. */
function fillKeys(keys: unknown[], bucket?: string): unknown[] {
  const ds = keys.filter((k): k is string => typeof k === "string" && /^\d{4}-\d{2}-\d{2}$/.test(k)).sort();
  if (!bucket || ds.length !== keys.length || ds.length < 2) return keys;
  const out: string[] = [];
  let d = new Date(ds[0] + "T00:00:00Z");
  const end = new Date(ds.at(-1)! + "T00:00:00Z");
  while (d <= end && out.length < 400) {
    out.push(d.toISOString().slice(0, 10));
    d = bucket === "day" ? new Date(d.getTime() + 864e5) : bucket === "week" ? new Date(d.getTime() + 7 * 864e5) : new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + (BUCKET_MONTHS[bucket] ?? 1), 1));
  }
  return out;
}

export function seriesOf(res: XResult): { x: string[]; keys: unknown[]; skeys: (unknown[] | null)[]; series: Series[]; cols: XOutColumn[] } | null {
  if (res.kind === "grouped") {
    if (!res.groups.length) return null;
    const rows = res.rows.filter((r) => r.level === res.groups.length);
    const by = new Map(rows.map((r) => [String(r.keys[0]), r]));
    const keys = fillKeys(rows.map((r) => r.keys[0]), res.groups[0].bucket);
    return { x: keys.map((k) => fmtKey(k, res.groups[0])), keys, skeys: res.values.map(() => null), cols: res.values,
      series: res.values.map((v, i) => ({ name: v.name.replace(/^Sum of /, ""), values: keys.map((k) => by.get(String(k))?.values[i] ?? (additive(v) ? 0 : null)) })) };
  }
  if (res.kind === "pivot") {
    const rows = res.rows.filter((r) => r.level === res.rowGroups.length);
    const by = new Map(rows.map((r) => [String(r.keys[0]), r]));
    const keys = fillKeys(rows.map((r) => r.keys[0]), res.rowGroups[0]?.bucket);
    const zero = additive(res.values[0]) ? 0 : null;
    // Series in order of their total, biggest first, so the ones that fold into Other are the smallest.
    const order = res.colKeys.map((k, j) => ({ k, j, t: Math.abs(res.colTotals[j]?.[0] ?? 0) })).sort((a, b) => b.t - a.t);
    return { x: keys.map((k) => fmtKey(k, res.rowGroups[0])), keys, skeys: order.map(({ k }) => k), cols: res.values.map(() => res.values[0]),
      series: order.map(({ k, j }) => ({ name: k.map((v, i) => fmtKey(v, res.colGroups[i])).join(" · "), values: keys.map((key) => by.get(String(key))?.cells[j]?.[0] ?? zero) })) };
  }
  return null;
}

export function ChartTile({ tile, run, height, onPick }: { tile: DTile; run: TileRun; height: number; onPick?: (p: Pick) => void }) {
  if (!run.main) return <Skeleton kind="chart" />;
  const s = seriesOf(run.main);
  if (!s) return <div className="chart-empty">Pick a column to group by.</div>;
  const format = tile.view?.format;
  const pct = s.cols.every((c) => (format ?? c.format) === "pct");
  const res = run.main;
  // Past eight series the chart folds the tail into Other, which isn't one thing to click into.
  const pick = onPick ? (i: number, k: number) => { if (s.series.length > 8 && k >= 7) return; const p = pickFromChart(tile.id, res, s.keys, s.skeys, i, k, s.series[k]?.values[i] ?? null); if (p) onPick(p); } : undefined;
  return <Chart type={tile.view?.chart ?? "column"} x={s.x} series={s.series} label={tile.title} height={height} target={tile.view?.target ?? null} pct={pct} dense onPick={pick}
    format={(v, k) => (tile.view?.chart === "donut" ? fmtCompact(v, format ?? s.cols[0]?.format) : fmt(v, s.cols[Math.min(k, s.cols.length - 1)], format))} />;
}

// ---------------- Ranked bars ----------------

export function BarsTile({ tile, run, to, onPick }: { tile: DTile; run: TileRun; to?: (key: unknown) => string | null; onPick?: (p: Pick) => void }) {
  const r = run.main;
  if (!r) return <Skeleton kind="list" />;
  if (r.kind !== "grouped" || !r.groups.length) return <div className="chart-empty">Group by a column to rank it.</div>;
  const col = r.values[0], format = tile.view?.format;
  let rows = r.rows.filter((x) => x.level === r.groups.length).map((x) => ({ key: x.keys[0], label: fmtKey(x.keys[0], r.groups[0]), v: x.values[0] ?? 0 }));
  const top = tile.view?.top ?? 8;
  const adds = additive(col);
  if (rows.length > top) {
    const rest = rows.slice(top);
    rows = rows.slice(0, top);
    if (adds) rows.push({ key: null, label: `Other (${rest.length})`, v: rest.reduce((t, x) => t + x.v, 0) });
  }
  if (!rows.length) return <div className="chart-empty">Nothing in the selected period.</div>;
  const max = Math.max(...rows.map((x) => Math.abs(x.v)), 1e-9);
  const neg = rows.some((x) => x.v < 0);
  const total = adds ? r.rows.filter((x) => x.level === r.groups.length).reduce((t, x) => t + (x.values[0] ?? 0), 0) : null;
  return (
    <div className={`rank ${neg ? "two" : ""}`} role="list">
      {rows.map((x, i) => {
        const w = (Math.abs(x.v) / max) * (neg ? 50 : 100);
        const href = x.key !== null && to ? to(x.key) : null;
        const other = x.label.startsWith("Other (");
        return (
          <div key={i} className={`rank-r ${onPick && !other ? "pickable" : ""}`} role="listitem" title={`${x.label}: ${fmt(x.v, col, format)}${total ? ` (${Math.round((x.v / total) * 100)}%)` : ""}`}
            onClick={onPick && !other ? () => onPick(pickOf(tile.id, [r.groups[0]], [x.key], 0, x.v)) : undefined}>
            <span className="rank-l">{href ? <Link to={href} onClick={(e) => e.stopPropagation()}>{x.label}</Link> : x.label}</span>
            <span className="rank-v">{fmtCompact(x.v, format ?? col.format)}{total && total > 0 && x.v > 0 ? <em>{Math.round((x.v / total) * 100)}%</em> : null}</span>
            <span className="rank-t"><i style={{ width: `${Math.max(0.5, w)}%`, ...(neg ? (x.v < 0 ? { right: "50%" } : { left: "50%" }) : {}), background: other ? "#A19F9D" : x.v < 0 ? PALETTE[1] : PALETTE[0] }} /></span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------- Table ----------------

export function TableTile({ tile, run, onPick }: { tile: DTile; run: TileRun; onPick?: (p: Pick) => void }) {
  const r = run.main;
  if (!r) return <Skeleton kind="list" />;
  const bars = tile.view?.bars !== false;
  if (r.kind === "table") {
    if (!r.rows.length) return <div className="chart-empty">No rows in the selected period.</div>;
    return (
      <div className="dt-wrap"><table className="dt">
        <thead><tr>{r.columns.map((c) => <th key={c.id} className={c.type === "number" ? "r" : ""}>{c.name}</th>)}</tr></thead>
        <tbody>{r.rows.map((row, i) => <tr key={i}>{row.map((v, j) => <td key={j} className={r.columns[j].type === "number" ? "r num" : ""}>{fmt(v, r.columns[j])}</td>)}</tr>)}</tbody>
        {r.total > r.rows.length && <tfoot><tr><td colSpan={r.columns.length} className="muted">Showing {r.rows.length} of {r.total.toLocaleString("en-US")} rows</td></tr></tfoot>}
      </table></div>
    );
  }
  if (r.kind !== "grouped") return <div className="chart-empty">This tile shows a table or grouped table.</div>;
  const n = r.groups.length;
  const detail = r.rows.filter((x) => x.level === n);
  if (!detail.length) return <div className="chart-empty">No rows in the selected period.</div>;
  const barCol = bars ? r.values.findIndex((v) => (v.format === "money" || v.format === "qty") && additive(v)) : -1;
  const max = barCol < 0 ? 0 : Math.max(...detail.map((x) => Math.abs(x.values[barCol] ?? 0)), 1e-9);
  const grand = r.rows.find((x) => x.level === 0 && n > 0);
  const vname = (v: XOutColumn) => v.name.replace(/^Sum of /, "").replace(/^Distinct of Document No$/, "Documents");
  return (
    <div className="dt-wrap"><table className="dt">
      <thead><tr>{r.groups.map((g) => <th key={g.id}>{g.name}</th>)}{r.values.map((v, i) => <th key={i} className="r">{vname(v)}</th>)}</tr></thead>
      <tbody>{r.rows.filter((x) => x.level > 0 || n === 0).map((x, i) => {
        const sub = x.level < n;
        return (
          <tr key={i} className={`${sub ? "sub" : ""} ${onPick ? "pickable" : ""}`} onClick={onPick ? () => onPick(pickOf(tile.id, r.groups.slice(0, x.level), x.keys.slice(0, x.level), 0, x.values[0])) : undefined}>
            {r.groups.map((g, j) => <td key={j}>{sub ? (j === x.level - 1 ? `${fmtKey(x.keys[j], g)} Total` : "") : fmtKey(x.keys[j], g)}</td>)}
            {x.values.map((v, j) => <td key={j} className="r num">{j === barCol && !sub ? <span className="dbar"><i style={{ width: `${(Math.abs(v ?? 0) / max) * 100}%` }} /><span>{fmt(v, r.values[j])}</span></span> : fmt(v, r.values[j])}</td>)}
          </tr>
        );
      })}</tbody>
      {grand && <tfoot><tr>{r.groups.map((_, j) => <td key={j}>{j === 0 ? "Total" : ""}</td>)}{grand.values.map((v, j) => <td key={j} className="r num">{fmt(v, r.values[j])}</td>)}</tr></tfoot>}
    </table></div>
  );
}

// ---------------- Heatmap ----------------

/** Sequential teal, light to dark; cells under a target take the warm ramp instead. */
const TEAL = ["#E3F2F3", "#B9DFE2", "#83C5CB", "#3FA2AC", "#007D89"];
const WARM = ["#FBE3D3", "#F5C2A0"];
export function HeatmapTile({ tile, run, onPick }: { tile: DTile; run: TileRun; onPick?: (p: Pick) => void }) {
  const r = run.main;
  if (!r) return <Skeleton kind="list" />;
  if (r.kind !== "pivot" || !r.colKeys.length) return <div className="chart-empty">A heatmap needs a row and a column to pivot on.</div>;
  const col = r.values[0], format = tile.view?.format ?? col.format, target = tile.view?.target ?? null;
  const rows = r.rows.filter((x) => x.level === r.rowGroups.length).sort((a, b) => Math.abs(b.total[0] ?? 0) - Math.abs(a.total[0] ?? 0)).slice(0, 14);
  const vals = rows.flatMap((x) => x.cells.map((c) => c?.[0] ?? null)).filter((v): v is number => v !== null && Number.isFinite(v));
  const lo = Math.min(...vals), hi = Math.max(...vals);
  const tone = (v: number | null) => {
    if (v === null || !Number.isFinite(v)) return { background: "transparent", color: "var(--fg-3)" };
    if (target !== null && v < target) return { background: WARM[v < target - (target - lo) / 2 ? 1 : 0], color: "#5C2A0E" };
    const t = hi === lo ? 0.5 : (v - (target ?? lo)) / (hi - (target ?? lo));
    const k = Math.max(0, Math.min(TEAL.length - 1, Math.floor(t * TEAL.length)));
    return { background: TEAL[k], color: k >= 3 ? "#FFFFFF" : "#0E3B40" };
  };
  const cols = r.colKeys.slice(-12);
  const off = r.colKeys.length - cols.length;
  return (
    <div className="hm-wrap">
      <table className="hm">
        <thead><tr><th>{r.rowGroups[0]?.name}</th>{cols.map((k, j) => <th key={j}>{k.map((v, i) => fmtKey(v, r.colGroups[i])).join(" · ").replace(/^(\w{3}) 20(\d\d)$/, "$1 $2")}</th>)}</tr></thead>
        <tbody>{rows.map((x, i) => (
          <tr key={i}><th className={onPick ? "pickable" : ""} onClick={onPick ? () => onPick(pickOf(tile.id, [r.rowGroups[0]], [x.keys[0]], 0, x.total[0])) : undefined}>{fmtKey(x.keys[0], r.rowGroups[0])}</th>
            {cols.map((ck, j) => { const v = x.cells[j + off]?.[0] ?? null; return <td key={j} style={tone(v)} title={v === null ? "No sales" : fmt(v, col, format)} className={onPick && v !== null ? "pickable" : ""}
              onClick={onPick && v !== null ? () => onPick(pickOf(tile.id, [r.rowGroups[0], ...r.colGroups], [x.keys[0], ...ck], 0, v)) : undefined}>{v === null ? "" : format === "pct" ? (v * 100).toFixed(1) : fmtCompact(v, format)}</td>; })}</tr>
        ))}</tbody>
      </table>
      <div className="hm-legend">
        {target !== null && <span><i style={{ background: WARM[1] }} />Below {format === "pct" ? `${Math.round(target * 100)}%` : fmtCompact(target, format)}</span>}
        <span className="hm-ramp">{fmtCompact(target ?? lo, format)}{TEAL.map((c) => <i key={c} style={{ background: c }} />)}{fmtCompact(hi, format)}</span>
      </div>
    </div>
  );
}

export function TextTile({ tile }: { tile: DTile }) {
  return <div className="txt">{(tile.view?.text ?? "").split(/\n{2,}/).map((p, i) => <p key={i}>{p.split(/(\*\*[^*]+\*\*)/).map((s, j) => s.startsWith("**") ? <strong key={j}>{s.slice(2, -2)}</strong> : s)}</p>)}</div>;
}

export function Skeleton({ kind }: { kind: "chart" | "list" }) {
  return <div className={`sk-wrap ${kind}`} aria-label="Loading">{Array.from({ length: kind === "chart" ? 8 : 5 }, (_, i) => <span key={i} className="sk" style={kind === "chart" ? { height: `${30 + ((i * 37) % 60)}%` } : { width: `${90 - i * 12}%` }} />)}</div>;
}

export type { DSlicer };
